'use client';

import { motion } from 'motion/react';
import { useTheme } from '~/hooks';
import Image from 'next/image';
import logoDark from '~/assets/images/logo-white.png';
import logoLight from '~/assets/images/logo-black.png';
import { DarkModeToggle } from '~/components/DarkModeToggle';
import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';

export const AuthHeader = () => {
  const { isDark } = useTheme();

  return (
    <>
      {/* Animated Background Elements */}
      <motion.div
        className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -40, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

      {/* Floating SVG Art - User Profile & Stars Theme */}
      <motion.div
        className="absolute top-10 right-20 w-56 h-56 opacity-30 pointer-events-none z-0"
        animate={{
          y: [0, -15, 0],
          rotate: [0, -5, 0],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg
          viewBox="0 0 200 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* User avatar circle */}
          <circle
            cx="100"
            cy="80"
            r="35"
            stroke="#3b82f6"
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx="100"
            cy="75"
            r="15"
            stroke="#3b82f6"
            strokeWidth="3"
            fill="none"
          />
          <path
            d="M 70 105 Q 70 90 85 90 L 115 90 Q 130 90 130 105"
            stroke="#3b82f6"
            strokeWidth="3"
            fill="none"
          />

          {/* Stars around */}
          <path
            d="M 40 40 L 42 48 L 50 50 L 42 52 L 40 60 L 38 52 L 30 50 L 38 48 Z"
            fill="#3b82f6"
          />
          <path
            d="M 160 30 L 162 38 L 170 40 L 162 42 L 160 50 L 158 42 L 150 40 L 158 38 Z"
            fill="#3b82f6"
          />
          <path
            d="M 170 120 L 172 128 L 180 130 L 172 132 L 170 140 L 168 132 L 160 130 L 168 128 Z"
            fill="#3b82f6"
          />
        </svg>
      </motion.div>

      <motion.div
        className="absolute bottom-10 left-20 w-52 h-52 opacity-30 pointer-events-none z-0"
        animate={{
          y: [0, 20, 0],
          rotate: [0, 4, 0],
        }}
        transition={{
          duration: 6.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg
          viewBox="0 0 200 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Rocket / Launch theme */}
          <path
            d="M 100 20 L 120 100 L 110 100 L 110 140 L 100 160 L 90 140 L 90 100 L 80 100 Z"
            stroke="#3b82f6"
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx="100"
            cy="50"
            r="8"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 75 120 L 70 140 L 90 130 Z"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 125 120 L 130 140 L 110 130 Z"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />

          {/* Smoke/clouds */}
          <ellipse
            cx="100"
            cy="170"
            rx="20"
            ry="8"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />
          <ellipse
            cx="85"
            cy="180"
            rx="15"
            ry="6"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />
          <ellipse
            cx="115"
            cy="180"
            rx="15"
            ry="6"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </motion.div>

      {/* Header */}
      <header className="border-b border-card-border bg-card/50 backdrop-blur-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              href="/"
              className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Image
                src={isDark ? logoDark : logoLight}
                alt="Leadbunker"
                className="h-10 w-auto"
                width={200}
                height={100}
              />
            </Link>

            <div className="flex items-center gap-4">
              <DarkModeToggle />

              <Link
                href="/"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary hover:bg-background-secondary transition-colors text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
